import * as Sentry from '@sentry/react';
import { createBrowserRouter } from 'react-router';

import { store } from 'config/store/configureStore';
import { locationChange } from 'modules/router/actions';
import { registerRouter } from 'modules/router/navigate';
import { registerRoutes } from 'modules/router/routeRegistry';

import { routes } from './routes';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter(routes);

registerRouter(router);
registerRoutes(routes);

store.dispatch(locationChange(router.state.location, router.state.historyAction));

/**
 * Keeps the Redux location mirror in step with the router.
 *
 * Timing contract worth knowing: the data router only writes `state.location` in `completeNavigation`,
 * so `LOCATION_CHANGE` lands *after* the route's `lazy` chunk has downloaded — not when the navigation
 * starts, as `redux-first-history` used to. Page workers keyed on it via `scopeToPage` therefore issue
 * their fetches after the chunk instead of alongside it, which costs one extra round of latency on the
 * first visit to a route (nothing on a warm chunk, where `lazy` settles in a microtask).
 *
 * Dispatching earlier, off `state.navigation.location`, would restore the overlap but is NOT a drop-in
 * change: many reducers reset their slice on `LOCATION_CHANGE`, and firing it before the router commits
 * would wipe the outgoing page's state while it is still on screen.
 *
 * The dedupe on `location.key` is what keeps this to one dispatch per navigation: `subscribe` also fires
 * for `navigation`, `revalidation`, fetcher and blocker transitions.
 */
let previousLocationKey = router.state.location.key;
router.subscribe(state => {
  if (state.location.key === previousLocationKey) {
    return;
  }
  previousLocationKey = state.location.key;
  store.dispatch(locationChange(state.location, state.historyAction));
});
